import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCopyToClipboard, useLocation, useToggle } from 'react-use';

import { ReferralQuickSummaryItem } from '../types/';

import { IProfile } from 'models/profile/i-profile.interface';
import { IState } from 'models/reducers/i-state.interface';

import { ROUTES } from 'constants/routes.constant';

import { setShowSelectWalletModal } from 'actions/global-actions';

import Button from 'components/buttons/Button';
import { GlassBox } from 'components/glassBox/GlassBox';
import Spacing from 'components/spacing/Spacing';
import { SummaryItem } from 'components/summaryItem/SummaryItem';
import Tooltip from 'components/tooltip/Tooltip';

import { useConnectWalletEvents } from 'hooks/events/useConnectWalletEvents';
import { useVipLevelsQuery } from 'hooks/queries/useVipLevelsQuery';
import { useVipUserQuery } from 'hooks/queries/useVipUserQuery';
import { getNextLevelReq, formatProgressValue, formatThreshold, isWeiMetric, fromWei, getMetricUnit } from 'routes/vip/vip.utils';

import { centralEllipsis } from 'utils/ellipsis.utils';
import { numberFormat } from 'utils/number-format.utils';

import ProfileAvatarReferral from 'assets/profile/icons/profile-avatar-referral-clean.svg';
import profilePencilIcon from 'assets/profile/icons/profile-pencil.svg';
import SingleEventCheckedIcon from 'assets/single-event/icons/checked.svg';
import SingleEventVipIcon from 'assets/single-event/icons/vip.svg';

import { ReferralProfileEditModal } from './ReferralProfileEditModal';

type QuickSummaryStatsProps = {
  ctaLabel: string;
  items: ReferralQuickSummaryItem[];
};

export const QuickSummaryStats = ({ ctaLabel, items }: QuickSummaryStatsProps) => {
  const profile = useSelector(({ profile }: IState): IProfile | null => profile.profile);
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const { origin } = useLocation();
  const { balance: vipPointsBalance, nextLevel: apiNextLevel } = useVipUserQuery();
  const { currentLevel } = useVipLevelsQuery();
  const dispatch = useDispatch();
  const { sendConnectWalletClickedEvent } = useConnectWalletEvents();

  const [profileEditModal, toggleProfileEditModal] = useToggle(false);
  const [clipboardState, copyToClipboard] = useCopyToClipboard();

  const isLoggedIn = !!walletAddress && !!profile;

  const resolvedLevel = currentLevel ?? null;
  const isVipLevelAchieved = resolvedLevel !== null;
  const nextLevelReq = getNextLevelReq(apiNextLevel);
  const metricUnit = nextLevelReq ? getMetricUnit(nextLevelReq.metricKey) : undefined;

  const progressRaw = nextLevelReq ? parseFloat(nextLevelReq.userProgress) : (vipPointsBalance ?? 0);
  const thresholdRaw = nextLevelReq ? parseFloat(nextLevelReq.threshold) : 0;
  const progressDisplay = nextLevelReq
    ? formatProgressValue(nextLevelReq.userProgress, nextLevelReq.metricKey)
    : undefined;
  const thresholdDisplay = nextLevelReq
    ? (isWeiMetric(nextLevelReq.metricKey) ? formatThreshold(String(fromWei(nextLevelReq.threshold))) : formatThreshold(nextLevelReq.threshold))
    : undefined;

  const progressPercentage = useMemo(() => {
    if (!nextLevelReq) return 100;
    if (thresholdRaw === 0) return 0;
    return Math.min((progressRaw / thresholdRaw) * 100, 100);
  }, [nextLevelReq, progressRaw, thresholdRaw]);

  const referralLink = useMemo(() => {
    if (!walletAddress) return '';
    return [origin, ROUTES.STORE, '?referrerWallet=', walletAddress].join('');
  }, [origin, walletAddress]);

  const dateJoined = useMemo(() => {
    if (!profile?.date_joined) {
      return 'xx.xx.xxxx';
    }

    return DateTime.fromMillis(new Date(profile.date_joined).getTime()).toFormat('dd.MM.yyyy');
  }, [profile?.date_joined]);

  const onClickReferralLinkCopy = () => {
    copyToClipboard(referralLink);
  };

  const onClickConnectWallet = () => {
    dispatch(setShowSelectWalletModal(true));
    sendConnectWalletClickedEvent();
  };

  const userName = profile?.first_name || 'Unknown';
  const userWallet = walletAddress ? centralEllipsis(walletAddress, 4) : 'XXXX...XXX';
  const userJoined = dateJoined;

  return (
    <div>
      {profileEditModal && <ReferralProfileEditModal onClose={toggleProfileEditModal} />}
      {isLoggedIn ? (
        <div className="tw-flex tw-flex-col tw-gap-[16px] tw-px-[22px] lg:tw-flex-row lg:tw-gap-[50px] lg:tw-px-0">
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[30px] lg:tw-flex-row">
            <div className="tw-flex tw-h-[140px] tw-w-[140px] tw-flex-col tw-items-center tw-justify-center tw-gap-[20px] tw-rounded-full tw-border-2 tw-border-[#323232] tw-bg-[#0E1010] tw-p-[20px] tw-shadow-[inset_0_0_25.7px_0_rgba(140,140,140,0.30)]">
              <img
                src={profile?.avatar || ProfileAvatarReferral}
                alt="profile avatar"
                className="tw-h-[115px] tw-w-[115px]"
              />
            </div>
            <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-[30px] lg:tw-max-w-[625px] lg:tw-items-start">
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[20px] lg:tw-flex-row lg:tw-gap-[50px]">
                <div className="tw-flex tw-items-center tw-justify-center">
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-[4px]">
                    {isVipLevelAchieved && (
                      <img
                        src={SingleEventVipIcon}
                        alt="vip"
                        className="tw-h-[30px] tw-w-[30px] lg:tw-h-[44px] lg:tw-w-[50px]"
                      />
                    )}
                    <span
                      className="tw-line-clamp-1 tw-text-[32px] tw-font-bold tw-text-white lg:tw-text-[24px] lg:tw-font-medium"
                      style={{
                        wordBreak: 'break-word',
                      }}
                    >
                      {userName}
                    </span>
                  </div>
                  <img
                    src={profilePencilIcon}
                    alt="profile pencil"
                    className="tw-ml-[20px] tw-h-[28px] tw-w-[28px] tw-cursor-pointer lg:tw-h-[50px] lg:tw-w-[50px]"
                    onClick={toggleProfileEditModal}
                  />
                </div>
                <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-[20px] tw-text-[12px] tw-text-white lg:tw-text-[20px]">
                  <span className="tw-uppercase">{userWallet}</span>
                  <span className="tw-h-[20px] tw-w-[2px] tw-bg-white lg:tw-h-[48px]" />
                  <div>
                    Joined <span className="tw-uppercase">{userJoined}</span>
                  </div>
                </div>
              </div>
              <GlassBox
                gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
                borderWidth="1px"
                className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-center tw-gap-[20px] tw-px-[20px] tw-py-[12px] lg:tw-max-w-[625px]"
              >
                <span className="tw-text-[12px] tw-font-bold tw-text-[#2D5EFF] lg:tw-text-[20px] ">Referral Link</span>
                <span className="tw-line-clamp-1 tw-flex-1 tw-text-ellipsis tw-text-[12px] tw-text-white lg:tw-text-[20px]">
                  {referralLink}
                </span>
                <Button
                  text={clipboardState.value ? 'Copied!' : 'Copy'}
                  size="medium-small"
                  color="referral-cta"
                  className="tw-w-[80px] tw-min-w-[80px] lg:tw-w-[120px] lg:tw-min-w-[100px]"
                  contentClassName="tw-text-[15px] lg:tw-text-[18px] tw-normal-case tw-font-semibold"
                  onClick={onClickReferralLinkCopy}
                />
              </GlassBox>
            </div>
          </div>
          <GlassBox
            gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
            borderWidth="1px"
            className="tw-flex tw-w-full tw-flex-col tw-justify-center tw-gap-[32px] tw-px-[20px] tw-py-[20px]"
          >
            <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-text-[16px] tw-font-semibold tw-text-white">
              <span>VIP Level Progress</span>
              <span>
                {progressDisplay && thresholdDisplay ? `${progressDisplay} / ${thresholdDisplay}` : progressDisplay ?? numberFormat(vipPointsBalance ?? 0)}
              </span>
            </div>

            <div className="tw-flex tw-w-full tw-items-center tw-gap-[10px]">
              <div className="tw-relative tw-h-2 tw-flex-1 tw-overflow-hidden tw-rounded-xl tw-bg-[#323232]">
                <div
                  className="tw-absolute tw-left-0 tw-top-0 tw-h-2 tw-rounded-xl tw-bg-gamefieldProgressGradient tw-transition-all tw-duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="tw-flex tw-items-center tw-justify-center">
                <span className="tw-text-center tw-text-[16px] tw-font-semibold tw-leading-none tw-text-white">
                  {isVipLevelAchieved ? `${resolvedLevel} VIP LVL` : 'NO VIP'}
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-[8px]">
                {isVipLevelAchieved && (
                  <img src={SingleEventCheckedIcon} alt="checked" className="tw-h-[20px] tw-w-[28px]" />
                )}
                <Tooltip
                  className="tw-cursor-help"
                  wrapperClassName="tw-flex tw-items-center"
                  contentClassName="tw-w-72 bp-md:tw-w-96"
                  text={metricUnit
                    ? `${metricUnit} required for the next level. Level up and unlock exclusive perks, events, and tournaments.`
                    : 'Level up and unlock exclusive perks, events, and tournaments. Add your Telegram username in Account Settings to join the VIP Telegram group.'}
                  position="left"
                  iconClassName="tw-text-[#2D5EFF]"
                  variant="glass"
                />
              </div>
            </div>
          </GlassBox>
        </div>
      ) : (
        <Button
          text={ctaLabel}
          size="medium"
          color="referral-cta"
          className="tw-w-[180px] tw-self-center tw-px-0"
          contentClassName="tw-text-[15px] lg:tw-text-[20px] tw-normal-case tw-font-semibold"
          onClick={onClickConnectWallet}
        />
      )}
      <Spacing height={40} />

      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[16px] lg:tw-flex-row lg:tw-flex-wrap lg:tw-gap-x-[100px] lg:tw-gap-y-[40px]">
        {items.map((item, index) => (
          <SummaryItem key={`${item.title}-${index}`} title={item.title} value={item.value} />
        ))}
      </div>
    </div>
  );
};
